import React from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'

import { Button, ButtonType, Separator, Icon } from '@junglescout/edna'

import { TEXT_STYLES } from 'TEXT_STYLES'
import { PLANS, PLAN_DETAILS } from 'constants/plan_details'
import { sendCtaClickEvent } from 'helpers/segment'

const Wrapper = styled.div`
  width: 188px;
  place-self: flex-end;
  border-radius: 4px;
  border: 1px solid
    ${props =>
      props.isActive
        ? props.theme.colors.primary
        : props.theme.colors.coolGrey300};
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 12px;
`
const Highlighted = styled.div`
  ${TEXT_STYLES.ButtonWhite}
  font-size: 11px !important;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.orange400};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 26px;
  text-transform: uppercase;
`
const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const Plan = styled.div`
  ${TEXT_STYLES.BodyBlack}
  font-weight: 500 !important;
  text-transform: uppercase;
`
const Price = styled.div`
  & > .price {
    ${TEXT_STYLES.H2Black}
    font-size: 24px !important;
  }
  & > .price > .interval {
    ${TEXT_STYLES.H2Black}
    font-size: 12px !important;
  }
`
const FullAnnual = styled.div`
  ${TEXT_STYLES.H5Light}
  margin-top: 4px;
`
const Details = styled.div`
  width: 100%;
  div:nth-child(even) {
    background-color: ${props => props.theme.colors.coolGrey025};
  }
`
const Row = styled.div`
  ${TEXT_STYLES.BodyBlack}
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`

export const PlanDetails = ({ type, frequency }) => {
  const { t } = useTranslation(['generic', 'noPlanModal'])
  const { colors } = useTheme()

  const plan = PLANS[type]
  const isSuite = type === 'suite'
  const isMonthly = frequency === 'monthly'

  const handleBuyNow = () => {
    const destination = plan.link[frequency]

    sendCtaClickEvent({
      destination,
      text: 'Buy Now',
      location: 'No Plan Modal',
      extraProps: { plan: type, frequency }
    })

    window.open(destination, '_blank')
  }

  return (
    <Wrapper isActive={isSuite}>
      {isSuite && (
        <Highlighted>
          {t('noPlanModal:mostPopular', 'Most Popular')}
        </Highlighted>
      )}

      <Body>
        <PriceContainer>
          <Plan>{plan.name}</Plan>
          <Price>
            <div className="price">
              {plan.price[frequency]}
              <span className="interval">{isMonthly ? '/mo' : '/mo*'}</span>
            </div>
          </Price>
        </PriceContainer>

        <Separator margin="12px 0 0 0" />

        {!isMonthly && (
          <FullAnnual>
            *
            {t('noPlanModal:billedAt', 'Billed at {{value}}/yr', {
              value: plan.price.full
            })}
          </FullAnnual>
        )}

        <Button
          btnType={isSuite ? ButtonType.PRIMARY : ButtonType.SECONDARY}
          margin="12px 0"
          onClick={handleBuyNow}>
          {t('noPlanModal:buyNow', 'Buy Now')}
        </Button>

        <Details>
          {Object.keys(PLAN_DETAILS).map((feature, index) => {
            const limit = PLAN_DETAILS[feature][type]
            return (
              <Row key={index}>
                {limit === 'access' ? (
                  <Icon name="CHECK_CIRCLE" color={colors.green500} />
                ) : (
                  limit
                )}
              </Row>
            )
          })}
        </Details>
      </Body>
    </Wrapper>
  )
}

PlanDetails.propTypes = {
  type: PropTypes.oneOf(['basic', 'suite', 'pro']).isRequired,
  frequency: PropTypes.oneOf(['monthly', 'annually']).isRequired
}
